// =====================================================================
// Assessment Center — Student Registration.
// Fields: Full Name, Email, Password, Confirm Password.
// After success, auto-signs in and redirects to the Student Dashboard.
// =====================================================================
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthShell from "./AuthShell";
import { Button, Field, Input } from "../../shared/components/ui";
import { useAuth } from "../../shared/auth/AuthContext";

export default function StudentRegisterPage() {
  const { studentRegister } = useAuth();
  const navigate = useNavigate();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await studentRegister({ fullName, email, password });
      navigate("/student/assessment-center");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't create your account.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell title="Create Student Account" subtitle="Takes less than a minute.">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field label="Full Name" required>
          <Input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Jordan Blake" required />
        </Field>
        <Field label="Email" required>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required />
        </Field>
        <Field label="Password" required hint="At least 6 characters.">
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />
        </Field>
        <Field label="Confirm Password" required>
          <Input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={6}
          />
        </Field>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? "Creating account…" : "Create Account"}
        </Button>
      </form>

      <div className="mt-6 border-t border-border-soft pt-5 text-center">
        <p className="text-sm text-slate-500">
          Already have an account?{" "}
          <Link to="/login/student" className="font-medium text-navy-700 hover:underline">
            Log in
          </Link>
        </p>
      </div>
    </AuthShell>
  );
}
